import { ALL_PLANS } from "@/lib/planPricing";
/**
 * site-search-index.ts — SITE-SEARCH v0.01 (2026-05-29)
 *
 * Hand-curated index used by the global Cmd+K search field. Pure client-side
 * (fuse.js) so no edge fn is consumed. Sections drive both the grouping in
 * the dropdown UI and the result weighting (Dashboard core > Marketing).
 *
 * When adding new pages: pick a stable id, include the route, add 3-6
 * aliases that catch common misspellings + synonyms + acronyms, list the
 * real headings + tabs from the page so phrase-level matches land on the
 * right spot. Empty arrays are fine.
 */

export type SiteSearchSection =
  | "Dashboard"
  | "INVEST"
  | "Setup"
  | "Marketing";

export interface SiteSearchEntry {
  id: string;
  title: string;
  url: string;
  section: SiteSearchSection;
  aliases: string[];
  headings: string[];
  tabs: string[];
  description: string;
  /** Higher weight → ranked higher when fuse scores are similar. */
  weight: number;
}

export const SITE_SEARCH_INDEX: SiteSearchEntry[] = [
  // ── DASHBOARD CORE (weight 100) ─────────────────────────────────
  {
    id: "page:dashboard",
    title: "Dashboard",
    url: "/dashboard",
    section: "Dashboard",
    aliases: ["home", "overview", "main", "start", "inbox"],
    headings: ["Inbox", "Engagement Trail", "Signals"],
    tabs: [],
    description: "Unified inbox + at-a-glance read on things in motion",
    weight: 100,
  },
  {
    id: "page:my-den",
    title: "The Den",
    url: "/dashboard/my-den",
    section: "Dashboard",
    aliases: ["den", "my den", "founder den", "war room", "daily action"],
    headings: ["Engage today", "Ship one thing", "Your launch progress"],
    tabs: [],
    description: "Daily-action surface — three zones: engage, ship, launch",
    weight: 100,
  },
  {
    id: "page:dealflow",
    title: "Dealflow",
    url: "/dashboard/dealflow",
    section: "Dashboard",
    aliases: ["pipeline", "leads", "deals", "crm", "kanban"],
    headings: ["Ready", "Contacted", "Replied", "Converted"],
    tabs: ["List", "Kanban", "Simple", "Advanced"],
    description: "Pipeline + sequences in one view — list, kanban, simple, advanced",
    weight: 100,
  },
  {
    id: "page:engagement-trail",
    title: "Engagement Trail",
    url: "/dashboard/artifacts",
    section: "Dashboard",
    aliases: ["trail", "sent emails", "history", "artifacts", "activity log", "outreach history"],
    headings: ["Recent", "Drafted", "Sent"],
    tabs: [],
    description: "Activity timeline — every artifact, reply, send, mention",
    weight: 100,
  },
  {
    id: "page:geofixer",
    title: "GEOFixer",
    url: "/dashboard/geofixer",
    section: "Dashboard",
    aliases: ["geofixer", "geo", "ai visibility", "llm visibility", "geo score"],
    headings: ["GEO Score", "Citation Rate", "Mentioned vs Cited"],
    tabs: [],
    description: "Whether AI assistants name your brand when buyers ask, and which sources they cite",
    weight: 100,
  },
  {
    id: "page:seofixer",
    title: "SEOFixer Autopilot",
    url: "/dashboard/seofixer",
    section: "Dashboard",
    aliases: ["seofixer", "seo", "autopilot", "seo fixes", "rank tracker", "citation"],
    headings: ["Autopilot", "Rank tracker", "Pending fixes"],
    tabs: [],
    description: "Generates SEO fixes daily — meta, internal links, schema, content gaps",
    weight: 100,
  },
  {
    id: "page:vetting",
    title: "Vetting Suite",
    url: "/dashboard/vetting-suite",
    section: "Dashboard",
    aliases: ["vet", "vetter", "vetting", "snapshot", "dossier", "report", "due diligence", "battlecard", "battle card", "arsenal", "sales arsenal"],
    headings: ["Founder", "Investor", "Firm", "Methodology"],
    tabs: [],
    description: "Reverse due-diligence reports on founders, investors, firms",
    weight: 100,
  },
  {
    id: "page:people-finder",
    title: "Find People & Companies",
    url: "/dashboard/find-contact",
    section: "Dashboard",
    aliases: ["find people", "find companies", "search people", "people finder", "lookup"],
    headings: ["People", "Companies", "Recent searches"],
    tabs: [],
    description: "Search any name, surface verified profile + emails",
    weight: 100,
  },
  {
    id: "page:compare-people",
    title: "Compare People",
    url: "/dashboard/compare-people",
    section: "Dashboard",
    aliases: ["candidates", "compare people", "compare", "bulk compare", "talent finder", "hire"],
    headings: ["Compare", "Bulk compare", "Scorecard"],
    tabs: [],
    description: "Compare 2-3 people side by side or bulk-score 20+ prospects, partners or investors",
    weight: 100,
  },
  {
    id: "page:scans",
    title: "Scans",
    url: "/dashboard/scans",
    section: "Dashboard",
    aliases: ["scan", "mentions", "listening", "monitoring", "scan history", "new scan"],
    headings: ["Active", "History", "Suggestions"],
    tabs: [],
    description: "Run scans — Reddit, X, LinkedIn, podcasts, news",
    weight: 100,
  },
  {
    id: "page:brand-kit",
    title: "Brand Kit",
    url: "/dashboard/brand-kit",
    section: "Dashboard",
    aliases: ["brandkit", "brand", "branding kit", "brad kite", "brand book", "brand voice"],
    headings: ["About description", "ICP", "Core products", "Audience", "Pricing", "Feature sets", "Use cases"],
    tabs: [],
    description: "The source of truth your tools read from — ICP, products, voice, autofill",
    weight: 100,
  },
  {
    id: "page:company-profile",
    title: "Company Profile",
    url: "/dashboard/company-profile",
    section: "Dashboard",
    aliases: ["profile", "company", "my company", "company profile", "about"],
    headings: ["Company", "Founders", "Competitors", "Customers", "Voice"],
    tabs: [],
    description: "The company side of the source of truth — competitors, customers, voice",
    weight: 100,
  },
  {
    id: "page:saved-content",
    title: "Saved Content",
    url: "/dashboard/saved",
    section: "Dashboard",
    aliases: ["content", "saved", "library", "content library", "drafts"],
    headings: ["Drafts", "Sent", "Published"],
    tabs: [],
    description: "Everything you saved — clips, dossiers, drafts, reports",
    weight: 100,
  },
  {
    id: "page:content-creator",
    title: "Content Creator",
    url: "/dashboard/content-tools",
    section: "Dashboard",
    aliases: ["creator", "generate", "blog", "blog post", "landing page", "content tools"],
    headings: ["Blog post creator", "Landing page generator", "About page", "FAQ generator"],
    tabs: [],
    description: "Blog posts, landing pages, FAQs — wired to your brand kit",
    weight: 100,
  },
  {
    id: "page:integrations",
    title: "Integrations, APIs & AI Agents",
    url: "/dashboard/integrations",
    section: "Dashboard",
    aliases: ["api", "mcp", "agents", "integrations", "webhook", "zapier"],
    headings: ["Integrations", "APIs", "AI Agents", "MCP"],
    tabs: [],
    description: "Connect MentionFox to your tools — MCP, webhooks, AI agents",
    weight: 90,
  },

  // ── INVEST (weight 90) ──────────────────────────────────────────
  {
    id: "page:invest",
    title: "INVEST Overview",
    url: "/dashboard/invest",
    section: "INVEST",
    aliases: ["invest", "invest hub", "investor workspace"],
    headings: ["Briefing", "Suite"],
    tabs: [],
    description: "Your investor (or founder-raising) workspace — what needs attention today",
    weight: 95,
  },
  {
    id: "page:find-investors",
    title: "Find Investors",
    url: "/dashboard/invest/find-investors",
    section: "INVEST",
    aliases: ["investor search", "investor research", "find vc", "find investor", "vc search"],
    headings: ["Search", "Sectors", "Stage"],
    tabs: [],
    description: "Search 52,000+ verified investor theses — sector, stage, check size",
    weight: 90,
  },
  {
    id: "page:pipeline-match",
    title: "Pipeline Match",
    url: "/dashboard/invest/pipeline-match",
    section: "INVEST",
    aliases: ["match", "thesis match", "auto match", "pipeline match"],
    headings: ["Active deals", "Matched investors"],
    tabs: [],
    description: "Auto-match active deals to investors whose stated thesis fits",
    weight: 90,
  },
  {
    id: "page:warm-intros",
    title: "Warm Intros",
    url: "/dashboard/invest/warm-intros",
    section: "INVEST",
    aliases: ["intros", "warm", "warm path", "connector", "intro path", "introduction"],
    headings: ["Computed paths", "Manual paths"],
    tabs: [],
    description: "Shortest real path to reach someone — who you both know, ranked",
    weight: 90,
  },
  {
    id: "page:sector-theses",
    title: "Sector Theses",
    url: "/dashboard/invest/sector-theses",
    section: "INVEST",
    aliases: ["thesis", "theses", "my thesis", "sector thesis", "thesis sectors"],
    headings: ["Sectors", "Top investors per sector"],
    tabs: [],
    description: "Where investors say they invest, mapped to where they actually invest",
    weight: 90,
  },
  {
    id: "page:linchpins",
    title: "Linchpins",
    url: "/dashboard/invest/linchpins",
    section: "INVEST",
    aliases: ["linchpin", "asymmetric", "plays", "edge"],
    headings: ["Active plays", "Synth thesis"],
    tabs: [],
    description: "Asymmetric plays only you can run — opportunities the crowd missed",
    weight: 90,
  },
  {
    id: "page:investor-circuit",
    title: "Investor Circuit",
    url: "/dashboard/invest/circuit",
    section: "INVEST",
    aliases: ["circuit", "co-invest", "co invest", "investor network"],
    headings: ["Co-investment graph", "Top connectors"],
    tabs: [],
    description: "Investors you co-invest with most often — for warm-intro paths",
    weight: 90,
  },
  {
    id: "page:founder-radar",
    title: "Founder Radar",
    url: "/dashboard/invest/founder-radar",
    section: "INVEST",
    aliases: ["radar", "new founders", "stealth founders", "yc batch", "early signals"],
    headings: ["Pattern", "Score"],
    tabs: [],
    description: "Founders before they show up in Crunchbase — pattern-matched signals",
    weight: 90,
  },
  {
    id: "page:portfolio",
    title: "Portfolio",
    url: "/dashboard/invest/portfolio",
    section: "INVEST",
    aliases: ["holdings", "my portfolio", "positions", "investments"],
    headings: ["Held positions", "Add investment"],
    tabs: [],
    description: "Companies you've invested in — track news, valuation, ownership",
    weight: 90,
  },
  {
    id: "page:tearsheet",
    title: "Portfolio Tear Sheet",
    url: "/dashboard/invest/portfolio/tearsheet",
    section: "INVEST",
    aliases: ["tearsheet", "tear sheet", "lp report", "lp update"],
    headings: ["Summary", "Allocation", "Positions", "Recent activity"],
    tabs: [],
    description: "One-page printable portfolio summary — print for LPs or yourself",
    weight: 85,
  },
  {
    id: "page:anti-portfolio",
    title: "Anti-Portfolio",
    url: "/dashboard/pipeline?tab=passed",
    section: "INVEST",
    aliases: ["passed", "anti portfolio", "rejected", "missed"],
    headings: ["Passed deals", "Verified outcomes"],
    tabs: [],
    description: "Deals you passed on — learn from the ones that got away",
    weight: 85,
  },
  {
    id: "page:term-sheets",
    title: "Term Sheets",
    url: "/dashboard/invest/term-sheets/new",
    section: "INVEST",
    aliases: ["termsheet", "term sheet", "nvca", "ts"],
    headings: ["Author", "Compare", "Red flags"],
    tabs: [],
    description: "Draft + compare term sheets side by side — see what a clause really costs",
    weight: 85,
  },
  {
    id: "page:deal-memos",
    title: "Deal Memos",
    url: "/dashboard/invest/memos",
    section: "INVEST",
    aliases: ["memos", "memo", "investment memo", "ic memo"],
    headings: ["Context pack", "Conviction", "Linchpins"],
    tabs: [],
    description: "Structured memo behind every yes — thesis, concerns, the path",
    weight: 85,
  },
  {
    id: "page:fund-sim",
    title: "Fund Returns Simulator",
    url: "/dashboard/invest/fund-sim",
    section: "INVEST",
    aliases: ["fund sim", "fund simulator", "monte carlo", "dpi", "irr", "fund returns"],
    headings: ["Engine A inputs", "Outcome dist", "Percentile bar"],
    tabs: [],
    description: "Monte Carlo on portfolio construction, wired to real deal data",
    weight: 90,
  },
  {
    id: "page:raise-sim",
    title: "Raise Simulator",
    url: "/dashboard/invest/raising-sim",
    section: "INVEST",
    aliases: ["raise sim", "raising sim", "fundraise sim", "dilution", "raising simulator"],
    headings: ["Matched investors", "VC math", "Dilution", "Term sheet preview"],
    tabs: [],
    description: "Model your raise the way an investor will model you",
    weight: 90,
  },
  {
    id: "page:signals",
    title: "Signals",
    url: "/dashboard/signals",
    section: "INVEST",
    aliases: ["feed", "watching", "digest", "activity", "pulse", "watch", "signal stream"],
    headings: ["Live Feed", "Watching", "Digest", "Markets"],
    tabs: ["Feed", "Watching", "Digest", "Markets"],
    description: "Investor activity feed + companies you're watching + email digest setting",
    weight: 90,
  },
  {
    id: "page:investor-updates",
    title: "Investor Updates",
    url: "/dashboard/invest/updates",
    section: "INVEST",
    aliases: ["updates", "cap table updates", "monthly update", "visible vc"],
    headings: ["Drafts", "Sent", "Recipients"],
    tabs: [],
    description: "Visible.vc-style monthly updates — formatted, tracked, easier than email",
    weight: 90,
  },
  {
    id: "page:decks",
    title: "Decks",
    url: "/dashboard/invest/decks",
    section: "INVEST",
    aliases: ["pitch deck", "deck tracking", "papermark", "deck share", "share deck"],
    headings: ["Upload", "Analytics", "Viewers"],
    tabs: [],
    description: "Share pitch deck as a tracked link — who read it, which pages",
    weight: 90,
  },
  {
    id: "page:datarooms",
    title: "Data Rooms",
    url: "/dashboard/invest/datarooms",
    section: "INVEST",
    aliases: ["diligence room", "dataroom", "data room", "vdr", "diligence"],
    headings: ["Manage", "NDA gate", "Analytics"],
    tabs: [],
    description: "Multi-doc diligence rooms — NDA gate + per-document time tracking",
    weight: 90,
  },
  {
    id: "page:outreach-templates",
    title: "Outreach Templates",
    url: "/dashboard/invest/outreach-templates",
    section: "INVEST",
    aliases: ["templates", "cold email", "outreach template", "starter templates"],
    headings: ["Templates", "Composer", "Send drawer"],
    tabs: [],
    description: "Reusable outreach templates with smart-field substitution",
    weight: 85,
  },
  {
    id: "page:investor-brief",
    title: "Investor Brief",
    url: "/dashboard/invest/find-investors",
    section: "INVEST",
    aliases: ["brief", "one-pager", "one pager", "pre-meeting", "investor one pager"],
    headings: ["Their thesis", "Recent investments", "Stages", "Warm paths", "Questions"],
    tabs: [],
    description: "Pre-meeting one-pager — thesis, portfolio, questions they'll ask",
    weight: 90,
  },

  // ── SETUP / ACCOUNT (weight 70) ─────────────────────────────────
  {
    id: "page:setup",
    title: "Setup",
    url: "/dashboard/settings",
    section: "Setup",
    aliases: ["setup", "settings", "config", "configuration"],
    headings: ["Account", "Notifications", "Integrations", "Billing"],
    tabs: [],
    description: "Configure account, notifications, integrations, billing",
    weight: 70,
  },
  {
    id: "page:profile-settings",
    title: "My Profile",
    url: "/dashboard/profile",
    section: "Setup",
    aliases: ["my profile", "profile", "account", "user profile"],
    headings: ["Name", "Email", "Photo"],
    tabs: [],
    description: "Your personal profile — name, email, photo, identity",
    weight: 70,
  },
  {
    id: "page:credits",
    title: "Credits & Billing",
    url: "/dashboard/settings?tab=billing",
    section: "Setup",
    aliases: ["billing", "subscription", "credits", "cr", "plan", "payment", "invoice"],
    headings: ["Plan", "Credits", "Invoices"],
    tabs: [],
    description: "Subscription tier + credit balance + buy credit packs",
    weight: 75,
  },
  {
    id: "page:apply-to",
    title: "Apply to Programs",
    url: "/apply-to",
    section: "Setup",
    aliases: ["accelerators", "yc", "techstars", "apply", "program", "cohort"],
    headings: ["Accelerators & Cohorts", "Grants & Non-Dilutive Funding"],
    tabs: [],
    description: "Apply to accelerators, contests, and grants — with selection-bias notes",
    weight: 80,
  },
  {
    id: "page:grants",
    title: "Grants & Non-Dilutive Funding",
    url: "/apply-to#grants-section",
    section: "Setup",
    aliases: ["grant", "grants", "grant finder", "funding", "sbir", "nih", "innovate uk", "non-dilutive"],
    headings: ["US SBIR", "EU EIC", "Israel IIA", "Canada IRAP"],
    tabs: [],
    description: "22 verified non-dilutive funding programs with concrete tips",
    weight: 80,
  },

  // ── MARKETING (weight 40) ───────────────────────────────────────
  {
    id: "page:why",
    title: "Why MentionFox",
    url: "/why",
    section: "Marketing",
    aliases: ["why", "why mentionfox", "about mentionfox"],
    headings: [],
    tabs: [],
    description: "The case for replacing a stack of point tools with one suite",
    weight: 40,
  },
  {
    id: "page:pricing",
    title: "Pricing",
    url: "/pricing",
    section: "Marketing",
    aliases: ["price", "pricing", "cost", "plans", "tiers"],
    headings: ["Free", "Pro", "Agency", "Enterprise"],
    tabs: [],
    description: ALL_PLANS.map((p) => `${p.name} ${p.monthlyPrice}`).join(" / "),
    weight: 50,
  },
  {
    id: "page:compare",
    title: "Compare Alternatives",
    url: "/compare",
    section: "Marketing",
    aliases: ["vs", "alternatives", "competitor", "compare", "versus"],
    headings: ["vs Brandwatch", "vs Meltwater", "vs Apollo"],
    tabs: [],
    description: "MentionFox side-by-side with Brandwatch, Meltwater, Apollo, and more",
    weight: 50,
  },
  {
    id: "page:case-studies",
    title: "Case Studies",
    url: "/case-studies",
    section: "Marketing",
    aliases: ["case study", "case studies", "outcomes", "results"],
    headings: ["Case studies"],
    tabs: [],
    description: "Real outcomes from agencies, founders, investors using MentionFox",
    weight: 40,
  },
  {
    id: "page:help",
    title: "Help Center",
    url: "/help",
    section: "Marketing",
    aliases: ["docs", "help", "support", "documentation", "guides"],
    headings: ["Getting started", "Workflows", "Industries"],
    tabs: [],
    description: "Help articles + playbooks + workflow guides",
    weight: 60,
  },
  {
    id: "page:vetting-methodology",
    title: "Vetting Methodologies",
    url: "/vetting",
    section: "Marketing",
    aliases: ["methodology", "vetting methodology", "how vetting works"],
    headings: ["Founder", "Investor", "Journalism", "Executive"],
    tabs: [],
    description: "How each vetting track works — sections, sources, scoring",
    weight: 40,
  },
];
